<template>
  <div class="staff-list">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">{{ $t('staff.title') }}</h2>
      <b-button variant="primary" @click="createStaff">
        <i class="fas fa-plus mr-2"></i>{{ $t('staff.create_button') }}
      </b-button>
    </div>
    <CrudTable :columns="fields" :data-sources="staff" :pagination="paginationInfo" :loader="isLoading"
      list-url="/staff" @search="handleSearch" @filter="handleFilter" @refresh="fetchStaff" ref="action-table">
      <template #cell(full_name)="data">
        <div class="font-weight-bold">{{ data.item.full_name || 'N/A' }}</div>
      </template>
      <template #cell(email)="data">
        <div>{{ data.item.email || 'N/A' }}</div>
      </template>
      <template #cell(phone)="data">
        <div>{{ data.item.phone || 'N/A' }}</div>
      </template>
      <template #cell(department)="data">
        <div>{{ data.item.department?.name || 'N/A' }}</div>
      </template>
      <template #cell(role)="data">
        <div>{{ data.item.roles && data.item.roles.length > 0 ? data.item.roles[0].name : 'N/A' }}</div>
      </template>
      <template #cell(actions)="data">
        <div class="d-flex gap-2">
          <b-button variant="outline-primary" size="sm" @click="editStaff(data.item)">
            <i class="fas fa-edit"></i>
          </b-button>
          <b-button variant="outline-danger" size="sm" @click="confirmDelete(data.item)">
            <i class="fas fa-trash"></i>
          </b-button>
        </div>
      </template>
    </CrudTable>
    <ConfirmDeleteModal v-if="selectedStaff" :show.sync="showDeleteModal" :item-id="selectedStaff.id"
      delete-url="/staff" :message="$t('staff.confirm_delete_message', { staff: selectedStaff.full_name })"
      success-message="$t('staff.deleted_successfully')" error-message="$t('common.something_went_wrong')"
      @deleted="handleItemDeleted" />
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import CrudTable from "@/components/CrudTable.vue";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal.vue";

export default {
  name: "StaffList",
  components: {
    CrudTable,
    ConfirmDeleteModal
  },
  data() {
    return {
      staff: [],
      isLoading: false,
      showDeleteModal: false,
      selectedStaff: null,
      currentPage: 1,
      perPage: 10,
      sortBy: "created_at",
      sortDesc: true,
      filters: {
        search: "",
      },
      fields: [
        { key: "full_name", label: this.$t("staff.table_header_name"), sortable: true, sortDirection: "asc" },
        { key: "email", label: this.$t("staff.table_header_email"), sortable: true },
        { key: "phone", label: this.$t("staff.table_header_phone"), sortable: false },
        { key: "department", label: this.$t("staff.table_header_department"), sortable: false },
        { key: "role", label: this.$t("staff.table_header_role"), sortable: false },
        { key: "actions", label: this.$t("common.actions") },
      ],
      paginationInfo: {
        from: 0,
        to: 0,
        total: 0,
      },
    };
  },
  methods: {
    handleSearch(search) {
      this.filters.search = search;
      this.currentPage = 1;
      this.fetchStaff();
    },
    handleFilter(filter) {
      this.filters = { ...this.filters, ...filter };
      this.currentPage = 1;
      this.fetchStaff();
    },
    createStaff() {
      this.$router.push("/admin/staff/create");
    },
    editStaff(staff) {
      this.$router.push(`/admin/staff/${staff.id}/edit`);
    },
    confirmDelete(staff) {
      this.selectedStaff = staff;
      this.showDeleteModal = true;
    },
    handleItemDeleted() {
      this.$refs['action-table'].refreshTableData();
      this.$bvToast.toast(this.$t('staff.deleted_successfully'), {
        title: this.$t('common.success'),
        variant: 'success',
        solid: true,
      });
    },
    async fetchStaff() {
      this.isLoading = true;
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
          sort_by: this.sortBy,
          sort_desc: this.sortDesc,
          ...this.filters,
        };

        const response = await request({
          method: "get",
          url: "/staff",
          params,
        });

        // Handle paginated response from API
        this.staff = response.data.data || [];
        this.paginationInfo = {
          from: response.data.from || 0,
          to: response.data.to || 0,
          total: response.data.total || 0,
        };
      } catch (error) {
        this.$bvToast.toast(this.$t('staff.fetch_error'), {
          title: this.$t('common.error'),
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  mounted() {
    this.fetchStaff();
  },
};
</script>
